/**
 * Dashboard Page
 *
 * Quick actions and overview.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

export function Dashboard() {
  const { tenantId, actorId, isAuthenticated } = useAuth();

  const navItems = [
    { to: '/console/assessments', title: 'Assessments', desc: 'Create and manage assessments', icon: 'clipboard-list' },
    { to: '/console/artifacts', title: 'Artifacts', desc: 'Upload and manage artifacts', icon: 'file-text' },
    { to: '/console/rubrics', title: 'Rubrics', desc: 'View scoring rubrics', icon: 'scale' },
    { to: '/console/scores', title: 'Scores', desc: 'Generate scores from transcripts', icon: 'bar-chart-2' },
    { to: '/console/subscriptions', title: 'Subscriptions', desc: 'Manage webhook subscriptions', icon: 'webhook' },
    { to: '/console/deliveries', title: 'Deliveries', desc: 'View and replay webhook deliveries', icon: 'send' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 8 }}>
          System Dashboard
        </h1>
        <p style={{ color: 'var(--slate-500)', fontSize: 15 }}>
          Internal administration and debugging tools
        </p>
      </div>

      {!isAuthenticated && (
        <div
          style={{
            marginBottom: 24,
            padding: '16px 20px',
            background: 'var(--red-50)',
            border: '1px solid var(--red-100)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <iconify-icon icon="lucide:alert-circle" width="20" height="20" style={{ color: 'var(--red-500)', marginTop: 2 }}></iconify-icon>
          <div>
            <strong style={{ color: 'var(--red-700)' }}>Authentication Required</strong>
            <p style={{ margin: '4px 0 0', color: 'var(--red-600)', fontSize: 14 }}>
              Please sign in to start using the console.
            </p>
          </div>
        </div>
      )}

      {/* Current Config Card */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>Current Configuration</h2>
        <div className="table-container" style={{ maxWidth: 500 }}>
          <table className="table">
            <tbody>
              <tr>
                <td style={{ fontWeight: 500, width: 150, color: 'var(--slate-600)' }}>API Prefix</td>
                <td className="mono" style={{ color: 'var(--slate-900)' }}>/api/v1/internal</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500, color: 'var(--slate-600)' }}>Company</td>
                <td className="mono" style={{ color: tenantId ? 'var(--slate-900)' : 'var(--red-500)' }}>
                  {tenantId || '(not set)'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500, color: 'var(--slate-600)' }}>Actor ID</td>
                <td className="mono" style={{ color: 'var(--slate-900)' }}>{actorId}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/console/assessments" className="btn btn-primary">
            <iconify-icon icon="lucide:plus" width="16" height="16"></iconify-icon>
            Create Assessment
          </Link>
          <Link to="/console/artifacts" className="btn btn-primary">
            <iconify-icon icon="lucide:upload" width="16" height="16"></iconify-icon>
            Upload Artifact
          </Link>
          <Link to="/console/scores" className="btn btn-primary">
            <iconify-icon icon="lucide:sparkles" width="16" height="16"></iconify-icon>
            Generate Score
          </Link>
          <Link to="/console/subscriptions" className="btn btn-tertiary">
            <iconify-icon icon="lucide:webhook" width="16" height="16"></iconify-icon>
            Create Subscription
          </Link>
          <Link to="/console/exports" className="btn btn-tertiary">
            <iconify-icon icon="lucide:download" width="16" height="16"></iconify-icon>
            Export Bundle
          </Link>
        </div>
      </div>

      {/* Navigation Grid */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>Navigation</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                padding: 20,
                background: 'var(--slate-50)',
                borderRadius: 16,
                textDecoration: 'none',
                transition: 'all 0.15s ease',
                border: '1px solid transparent',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = 'var(--sky-200)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--slate-50)';
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--sky-500)',
                    flexShrink: 0,
                  }}
                >
                  <iconify-icon icon={`lucide:${item.icon}`} width="20" height="20"></iconify-icon>
                </div>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--slate-900)', marginBottom: 4 }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 13, color: 'var(--slate-500)', margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Golden Path */}
      <div className="card">
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Golden Path</h2>
        <p style={{ color: 'var(--slate-500)', marginBottom: 16, fontSize: 14 }}>
          Follow these steps to complete the standard assessment workflow:
        </p>
        <ol style={{ paddingLeft: 20, lineHeight: 2.2, color: 'var(--slate-700)', fontSize: 14 }}>
          <li>
            <Link to="/console/assessments" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Create an assessment</Link>
          </li>
          <li>
            <Link to="/console/artifacts" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Upload a transcript artifact</Link>
            <span style={{ color: 'var(--slate-400)' }}> (text/plain)</span>
          </li>
          <li>Link the artifact to the assessment</li>
          <li>
            <Link to="/console/scores" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Generate a score</Link>
            <span style={{ color: 'var(--slate-400)' }}> using the transcript</span>
          </li>
          <li>View score results and evidence spans</li>
          <li>
            <Link to="/console/subscriptions" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Create a webhook subscription</Link>
            <span style={{ color: 'var(--slate-400)' }}> (optional)</span>
          </li>
          <li>
            <Link to="/console/deliveries" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Monitor deliveries</Link>
            <span style={{ color: 'var(--slate-400)' }}> (optional)</span>
          </li>
          <li>
            <Link to="/console/exports" style={{ color: 'var(--sky-600)', fontWeight: 500 }}>Export the assessment bundle</Link>
          </li>
        </ol>
      </div>
    </div>
  );
}
